import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { 
  User, 
  Briefcase, 
  DollarSign, 
  Clock, 
  Globe, 
  Mail, 
  Phone,
  Calendar,
  Award,
  MessageSquare,
  Image,
  Package,
  CalendarDays,
  FileText,
  Euro
} from "lucide-react";
import { format } from "date-fns";
import { ExpertApplication } from "@/types/admin";

interface DetailedApplicationViewerProps {
  application: ExpertApplication;
}

export default function DetailedApplicationViewer({ application }: DetailedApplicationViewerProps) {
  // Parse availability from JSON string
  const availability = application.availability_hours ? JSON.parse(application.availability_hours) : [];
  
  // Parse package pricing from JSON string
  const packagePricing = application.package_pricing ? JSON.parse(application.package_pricing) : {};
  
  // Map package IDs to readable names
  const packageNames: Record<string, string> = {
    'introductory': 'Introductory Consultation',
    'standard': 'Standard Support Package', 
    'premium': 'Premium Case Management'
  };

  // Map package IDs to descriptions
  const packageDescriptions: Record<string, string> = {
    'introductory': '30-45 minute video or phone call',
    'standard': 'Comprehensive consultation and support',
    'premium': 'Full guidance across a specific care placement or transition'
  };

  return (
    <div className="space-y-8">
      {/* Header Card */}
      <Card className="bg-white shadow-sm border p-8 mb-8">
        <CardHeader className="pb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {application.profile_image_url && (
                <img 
                  src={application.profile_image_url} 
                  alt="Profile" 
                  className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                />
              )}
              <div className="space-y-1">
                <CardTitle className="text-2xl font-bold">{application.full_name}</CardTitle>
                <p className="text-muted-foreground text-lg">{application.professional_title}</p>
              </div>
            </div>
            <Badge variant={
              application.status === 'approved' ? 'default' :
              application.status === 'pending' ? 'secondary' :
              application.status === 'rejected' ? 'destructive' : 'outline'
            } className="text-base px-4 py-2">
              {application.status.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6 divide-y divide-muted">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pb-6">
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-500" />
              <span className="text-base">{application.email}</span>
            </div>
            {application.phone && (
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5 text-blue-500" />
                <span className="text-base">{application.phone}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-500" />
              <span className="text-base">Applied {format(new Date(application.created_at), "MMM d, yyyy")}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Professional Details */}
      <Card className="bg-white shadow-sm border p-8 mb-8">
        <CardHeader className="pb-4">
          <CardTitle className="text-xl font-semibold flex items-center gap-2">
            <Briefcase className="h-6 w-6 text-blue-500" />
            Professional Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 divide-y divide-muted">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6">
            <div>
              <Label className="text-base font-semibold">Years of Experience</Label>
              <div className="flex items-center gap-2 mt-1">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <span className="text-base">{application.years_experience} years</span>
              </div>
            </div>
            <div>
              <Label className="text-base font-semibold">Average Hourly Rate</Label>
              <div className="flex items-center gap-2 mt-1">
                <DollarSign className="h-5 w-5 text-muted-foreground" />
                <span className="text-base">€{application.hourly_rate}/hour</span>
                <span className="text-xs text-muted-foreground">(calculated from package pricing)</span>
              </div>
            </div>
          </div>
          <div className="pt-6">
            <Label className="text-base font-semibold">Specializations</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {application.specializations.map((spec: string, index: number) => (
                <Badge key={index} variant="outline" className="text-base px-3 py-1">
                  {spec}
                </Badge>
              ))}
            </div>
          </div>
          <div className="pt-6">
            <Label className="text-base font-semibold">Professional Bio</Label>
            <div className="mt-2 p-4 bg-muted rounded-lg text-base">
              {application.bio}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Service Packages with Custom Pricing */}
      {application.preferred_meeting_types && application.preferred_meeting_types.length > 0 && (
        <Card className="bg-white shadow-sm border p-8 mb-8">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-semibold flex items-center gap-2">
              <Package className="h-6 w-6 text-green-500" />
              Service Packages & Pricing
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {application.preferred_meeting_types.map((packageId: string, index: number) => {
                const packagePrice = packagePricing[packageId];
                const hasCustomPrice = packagePrice && packagePrice > 0;
                
                return (
                  <div key={index} className="p-4 border rounded-lg bg-green-50">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-green-800">
                        {packageNames[packageId] || packageId}
                      </h4>
                      {hasCustomPrice && (
                        <div className="flex items-center gap-1">
                          <Euro className="h-4 w-4 text-green-600" />
                          <span className="font-semibold text-green-800">€{packagePrice}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-green-600 mb-2">
                      {packageDescriptions[packageId]}
                    </p>
                    {!hasCustomPrice && (
                      <p className="text-xs text-orange-600 italic">
                        No custom price set
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Package Pricing Summary */}
            {Object.keys(packagePricing).length > 0 && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h5 className="font-medium text-blue-800 mb-2">Package Pricing Summary</h5>
                <div className="space-y-2">
                  {Object.entries(packagePricing).map(([packageId, price]) => (
                    <div key={packageId} className="flex justify-between items-center">
                      <span className="text-sm text-blue-700">
                        {packageNames[packageId] || packageId}:
                      </span>
                      <span className="font-medium text-blue-800">€{price as number}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Availability */}
      {availability && availability.length > 0 && (
        <Card className="bg-white shadow-sm border p-8 mb-8">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-semibold flex items-center gap-2">
              <CalendarDays className="h-6 w-6 text-purple-500" />
              Availability
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {availability.map((avail: string, index: number) => (
                <Badge key={index} variant="outline" className="text-base px-3 py-1 bg-purple-50 text-purple-700 border-purple-200">
                  {avail === 'weekdays' && 'Weekdays'}
                  {avail === 'weekends' && 'Weekends'}
                  {avail === 'evenings' && 'Evenings'}
                  {avail === 'flexible' && 'Flexible Schedule'}
                  {!['weekdays', 'weekends', 'evenings', 'flexible'].includes(avail) && avail}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Professional Information */}
      {(application.linkedin_url || application.website_url || application.certifications?.length || application.cv_file_url) && (
        <Card className="bg-white shadow-sm border p-8 mb-8">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-semibold flex items-center gap-2">
              <Award className="h-6 w-6 text-blue-500" />
              Additional Professional Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* External Links */}
            {(application.linkedin_url || application.website_url) && (
              <div>
                <Label className="text-base font-semibold">External Links</Label>
                <div className="space-y-3 mt-2">
                  {application.linkedin_url && (
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium">LinkedIn Profile</span>
                      </div>
                      <a 
                        href={application.linkedin_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        View Profile →
                      </a>
                    </div>
                  )}
                  {application.website_url && (
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium">Personal Website</span>
                      </div>
                      <a 
                        href={application.website_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-green-600 hover:text-green-800 text-sm"
                      >
                        Visit Site →
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Certifications */}
            {application.certifications && application.certifications.length > 0 && (
              <div>
                <Label className="text-base font-semibold">Professional Certifications</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {application.certifications.map((cert: string, index: number) => (
                    <Badge key={index} variant="outline" className="text-base px-3 py-1 bg-blue-50 text-blue-700 border-blue-200">
                      {cert}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* CV Download */}
            {application.cv_file_url && (
              <div>
                <Label className="text-base font-semibold">CV/Resume</Label>
                <div className="mt-2">
                  <a 
                    href={application.cv_file_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                  >
                    <FileText className="h-4 w-4" />
                    Download CV/Resume →
                  </a>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Motivation Letter */}
      {application.motivation_letter && (
        <Card className="bg-white shadow-sm border p-8 mb-8">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-semibold flex items-center gap-2">
              <MessageSquare className="h-6 w-6 text-blue-500" />
              Motivation Letter
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-muted rounded-lg text-base whitespace-pre-wrap">
              {application.motivation_letter}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Application Timeline */}
      <Card className="bg-white shadow-sm border p-8 mb-8">
        <CardHeader className="pb-4">
          <CardTitle className="text-xl font-semibold flex items-center gap-2">
            <Clock className="h-6 w-6 text-blue-500" />
            Application Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 border-l-4 border-blue-500 bg-blue-50">
              <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full"></div>
              <div>
                <p className="text-base font-medium">Application Submitted</p>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(application.created_at), "MMM d, yyyy 'at' HH:mm")}
                </p>
              </div>
            </div>
            {application.reviewed_at && (
              <div className="flex items-center gap-3 p-4 border-l-4 border-green-500 bg-green-50">
                <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full"></div>
                <div>
                  <p className="text-base font-medium">Application Reviewed</p>
                  <p className="text-sm text-muted-foreground">
                    {format(new Date(application.reviewed_at), "MMM d, yyyy 'at' HH:mm")}
                  </p>
                  <p className="text-sm text-muted-foreground">Status: {application.status}</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
